import { useMemo, useState } from "react";

import { keyBy } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { getDefaultCellWidth } from "@features/sales-v3/hooks/use-table-columns";
import {
  type ColumnConfig,
  useTableState,
} from "@features/sales-v3/hooks/use-table-state";
import {
  MetricCategorizer,
  MetricCategory,
  type SalesPageMetricWithChildren,
} from "@features/sales-v3/utils/metric-categorizer";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { IconButton, PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { CategoriesPanel } from "./categories-panel";
import { ColumnOrderPanel } from "./column-order-panel";
import { MetricsPanel } from "./metric-panel/metrics-panel";

type Props = {
  metrics: SalesPageMetric[];
};

const getMetricName = (metric: SalesPageMetric) => {
  if (
    metric.metadata?.name &&
    metric.metadata?.type &&
    metric.metadata?.metricId
  ) {
    return `${metric.metadata.name} (${metric.metadata.type})`;
  } else if (metric.metadata?.name) {
    return metric.metadata.name;
  }

  return metric.name;
};

export const CustomizeTableModal = ({ metrics }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MetricCategory>(
    MetricCategory.ALL,
  );
  const [selectedColumnConfigs, setSelectedColumnConfigs] = useState<
    ColumnConfig[]
  >([]);

  const { columnConfigs, setColumnConfigs } = useTableState();
  const applyColumnConfigs = () => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Apply Changes",
      data: {
        pageContext: { selectedColumnConfigs },
      },
    });

    setColumnConfigs(selectedColumnConfigs);
    closeModal();
  };
  const openModal = () => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Open Modal",
      data: {
        pageContext: { columnConfigs },
      },
    });

    setSelectedColumnConfigs(columnConfigs);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Close Modal",
    });

    setSelectedCategory(MetricCategory.ALL);
    setSelectedColumnConfigs([]);
    setIsModalOpen(false);
  };

  const availableMetricIds = new Set(metrics.map(({ id }) => id));
  const metricIdsWithoutMiniGraph = new Set(
    metrics.filter((e) => e.metadata?.isBenchmark).map(({ id }) => id),
  );
  const metricCategories = new MetricCategorizer(metrics);
  const metricsById = useMemo(() => {
    return keyBy(metrics, "id");
  }, [metrics]);

  const selectMetric = (modifiedMetric: SalesPageMetric) => {
    const metric = metricsById[modifiedMetric.id];
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Select Metric",
      data: {
        pageContext: { metric },
      },
    });

    setSelectedColumnConfigs((prev) => [
      ...prev,
      {
        id: metric.id,
        name: getMetricName(metric),
        showMiniGraph: false,
        width: getDefaultCellWidth(metric.name),
      },
    ]);
  };

  const selectManyMetrics = (metrics: SalesPageMetricWithChildren[]) => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Select Many Metrics",
      data: {
        pageContext: { metrics },
      },
    });

    const metricsToSelect = metrics.reduce<SalesPageMetricWithChildren[]>(
      (acc, metric) => {
        if (metric.children) {
          return [...acc, metric, ...metric.children];
        }
        return [...acc, metric];
      },
      [],
    );

    const selectedColumnConfigSet = new Set(
      selectedColumnConfigs.map(({ id }) => id),
    );
    const columnsToBeSelected = metricsToSelect.filter(
      ({ id }) => !selectedColumnConfigSet.has(id),
    );
    setSelectedColumnConfigs((prev) => [
      ...prev,
      ...columnsToBeSelected.map((proposedMetric) => {
        const metric = metricsById[proposedMetric.id];
        return {
          id: metric.id,
          name: getMetricName(metric),
          showMiniGraph: false,
          width: getDefaultCellWidth(metric.name),
        };
      }),
    ]);
  };

  const deselectMetric = (metric: SalesPageMetric) => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Deselect Metric",
      data: {
        pageContext: { metric },
      },
    });

    setSelectedColumnConfigs((prev) =>
      prev.filter(({ id }) => id !== metric.id),
    );
  };

  const clearAllByCategory = (category: MetricCategory) => {
    prettyLogEventPage({
      domain: "Customize Table Modal",
      action: "onClick",
      additionalActionInfo: "Clear All By Category",
      data: {
        pageContext: { category },
      },
    });

    const metricsToFilter =
      metricCategories.getMetricsByCategoryUngrouped(category);
    const metricsToFilterSet = new Set(metricsToFilter.map(({ id }) => id));
    setSelectedColumnConfigs((prev) =>
      prev.filter(({ id }) => !metricsToFilterSet.has(id)),
    );
  };

  return (
    <>
      <IconButton
        className="font-semibold leading-5 tracking-wide"
        variant="secondary"
        leftIcon={<i className="fa-regular fa-sharp fa-table" />}
        onClick={openModal}
      >
        CUSTOMIZE TABLE
      </IconButton>
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        titleContent={<div className="font-bold">Customize table</div>}
        size="3xl"
        bodyClassName="h-[calc(100%-60px)] py-0"
        contentClassName="h-[calc(100%-40px)]"
      >
        <div className="grid grid-cols-[20%_1fr_30%] grid-rows-1 gap-4 h-[calc(100%-80px)]">
          <CategoriesPanel
            metricCategories={metricCategories}
            selectedCategory={selectedCategory}
            setSelectedCategory={(value) => {
              prettyLogEventPage({
                domain: "Customize Table Modal",
                action: "onClick",
                additionalActionInfo: "Select Category",
                data: {
                  pageContext: { category: value },
                },
              });

              setSelectedCategory(value);
            }}
          />
          <MetricsPanel
            selectedCategory={selectedCategory}
            metricCategories={metricCategories}
            selectedColumnConfigs={selectedColumnConfigs}
            selectManyMetrics={selectManyMetrics}
            selectMetric={selectMetric}
            clearAllByCategory={clearAllByCategory}
            deselectMetric={deselectMetric}
            closeModal={closeModal}
          />
          <ColumnOrderPanel
            availableMetricIds={availableMetricIds}
            metricIdsWithoutMiniGraph={metricIdsWithoutMiniGraph}
            selectedColumnConfigs={selectedColumnConfigs}
            setSelectedColumnConfigs={setSelectedColumnConfigs}
            metricsById={metricsById}
          />
        </div>
        <div className="pt-5 flex justify-end space-x-1">
          <TertiaryButton onClick={closeModal}>CANCEL</TertiaryButton>
          <PrimaryButton onClick={applyColumnConfigs}>APPLY</PrimaryButton>
        </div>
      </Modal>
    </>
  );
};
